<div class="user-profile-container mat-app-background">
  @if (isAuthenticated) {
    <div>
      <div class="profile-header">
        <h1 class="mat-headline-4">User Profile</h1>
        <mat-divider></mat-divider>
      </div>
      <div class="profile-content">
        <!-- User Profile Information (Read-only) -->
        <mat-card class="profile-card mat-elevation-z3">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>account_circle</mat-icon> Profile Information
            </mat-card-title>
            <mat-card-subtitle>Your Azure DevOps &amp; Platform Portal Information</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content>
            @if (userProfile && !(loadingService.loading$ | async)) {
              <div class="profile-info">
                <div class="info-grid">
                  <div class="info-section">
                    <div class="info-item">
                      <span class="info-label">Name:</span>
                      <span class="info-value">{{ userName }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Email:</span>
                      <span class="info-value">{{ userEmail }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Display Name:</span>
                      <span class="info-value">{{ userProfile.displayName }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">ID:</span>
                      <span class="info-value">{{ userProfile.id }}</span>
                    </div>
                  </div>
                  <div class="info-section">
                    <div class="info-label">Roles:</div>
                    <div class="role-chips">
                      <mat-chip-set>
                        @for (role of userRoles; track role) {
                          <mat-chip [matTooltip]="'Role: ' + role">
                            {{ role }}
                          </mat-chip>
                        }
                      </mat-chip-set>
                    </div>
                  </div>
                  <!-- Team Membership Section -->
                  <mat-divider class="section-divider"></mat-divider>
                  <div class="team-membership-section">
                    <h3 class="mat-subtitle-1">Memberships</h3>
                    @if (!(loadingService.loading$ | async) && !teamsLoadError) {
                      <div>
                        <!-- Use expansion panels for better organization -->
                        <mat-accordion>
                          <!-- Azure DevOps Teams -->
                          @if (userTeams.length > 0) {
                            <mat-expansion-panel expanded>
                              <mat-expansion-panel-header>
                                <mat-panel-title>
                                  <mat-icon class="team-icon">people</mat-icon>
                                  Azure DevOps Teams
                                </mat-panel-title>
                                <mat-panel-description>
                                  {{ userTeams.length }} team{{ userTeams.length !== 1 ? 's' : '' }}
                                </mat-panel-description>
                              </mat-expansion-panel-header>
                              <div class="team-chips">
                                <mat-chip-set>
                                  @for (team of userTeams; track team) {
                                    <mat-chip
                                      [matTooltip]="'Team: ' + team"
                                      color="primary"
                                      selected>
                                      <mat-icon class="team-icon">group</mat-icon>
                                      {{ team }}
                                    </mat-chip>
                                  }
                                </mat-chip-set>
                              </div>
                            </mat-expansion-panel>
                          }
                          <!-- Azure DevOps Groups -->
                          @if (userGroups.length > 0) {
                            <mat-expansion-panel>
                              <mat-expansion-panel-header>
                                <mat-panel-title>
                                  <mat-icon class="team-icon">folder_shared</mat-icon>
                                  Azure DevOps Groups
                                </mat-panel-title>
                                <mat-panel-description>
                                  {{ userGroups.length }} group{{ userGroups.length !== 1 ? 's' : '' }}
                                </mat-panel-description>
                              </mat-expansion-panel-header>
                              <div class="team-chips">
                                <mat-chip-set>
                                  @for (group of userGroups; track group) {
                                    <mat-chip
                                      [matTooltip]="'Group: ' + group"
                                      color="accent">
                                      <mat-icon class="team-icon">folder</mat-icon>
                                      {{ group }}
                                    </mat-chip>
                                  }
                                </mat-chip-set>
                              </div>
                            </mat-expansion-panel>
                          }
                          <!-- Carvana AD Groups -->
                          @if (carvanaADGroups.length > 0) {
                            <mat-expansion-panel>
                              <mat-expansion-panel-header>
                                <mat-panel-title>
                                  <mat-icon class="team-icon">security</mat-icon>
                                  Carvana AD Groups
                                </mat-panel-title>
                                <mat-panel-description>
                                  {{ carvanaADGroups.length }} group{{ carvanaADGroups.length !== 1 ? 's' : '' }}
                                </mat-panel-description>
                              </mat-expansion-panel-header>
                              <div class="team-chips">
                                <mat-chip-set>
                                  @for (group of carvanaADGroups; track group) {
                                    <mat-chip
                                      [matTooltip]="'AD Group: ' + group"
                                      color="warn">
                                      <mat-icon class="team-icon">security</mat-icon>
                                      {{ group }}
                                    </mat-chip>
                                  }
                                </mat-chip-set>
                              </div>
                            </mat-expansion-panel>
                          }
                        </mat-accordion>
                        <!-- No memberships message -->
                        @if (userTeams.length === 0 && userGroups.length === 0 && carvanaADGroups.length === 0) {
                          <div class="no-teams-message">
                            <mat-icon color="warn">info</mat-icon>
                            <p>You are not a member of any teams or groups in the {{ organization }} organization.</p>
                          </div>
                        }
                      </div>
                    } @else {
                      @if (loadingService.loading$ | async) {
                        <div class="teams-loading">
                          <mat-spinner diameter="24"></mat-spinner>
                          <span>Loading memberships...</span>
                        </div>
                      } @else {
                        <div class="teams-error">
                          <mat-icon color="warn">error</mat-icon>
                          <p>Failed to load team memberships.</p>
                          <button mat-button color="primary" (click)="loadTeamMemberships()">
                            <mat-icon>refresh</mat-icon> Retry
                          </button>
                        </div>
                      }
                    }
                  </div>
                </div>
              </div>
            } @else {
              <div class="loading-container">
                <mat-spinner diameter="40"></mat-spinner>
                <p>Loading profile information...</p>
              </div>
            }
          </mat-card-content>
        </mat-card>
        <!-- GitHub Account Section -->
        <mat-card class="profile-card mat-elevation-z3">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>developer_mode</mat-icon> GitHub Account
            </mat-card-title>
            <mat-card-subtitle>GitHub Integration for Repository Management</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content>
            <!-- Connected State -->
            @if (githubConnectionInfo.isConnected) {
              <div class="github-connected">
                <div class="github-user-info">
                  <div class="user-avatar-section">
                    @if (githubUser?.avatar_url) {
                      <img [src]="githubUser?.avatar_url"
                        [alt]="getGitHubDisplayName()"
                        alt="GitHub Avatar"
                        class="github-avatar"
                        >
                    }
                    <div class="user-details">
                      <h3 class="mat-subtitle-1">{{ getGitHubDisplayName() }}</h3>
                      <p class="mat-body-2 github-username">&#64;{{ githubUser?.login }}</p>
                      <p class="mat-caption last-sync">Last synced: {{ getLastSyncTime() }}</p>
                    </div>
                  </div>
                  @if (githubUser) {
                    <div class="github-stats">
                      <div class="stat-item">
                        <mat-icon>folder</mat-icon>
                        <span class="stat-value">{{ githubRepositories.length }}</span>
                        <span class="stat-label">Repositories</span>
                      </div>
                      <div class="stat-item">
                        <mat-icon>business</mat-icon>
                        <span class="stat-value">{{ githubOrganizations.length }}</span>
                        <span class="stat-label">Organizations</span>
                      </div>
                      <div class="stat-item">
                        <mat-icon>people</mat-icon>
                        <span class="stat-value">{{ githubUser.followers }}</span>
                        <span class="stat-label">Followers</span>
                      </div>
                    </div>
                  }
                  <!-- GitHub Organizations -->
                  @if (githubOrganizations.length > 0) {
                    <div class="github-organizations">
                      <h4 class="mat-subtitle-2">Organizations</h4>
                      <div class="org-chips">
                        <mat-chip-set>
                          @for (org of githubOrganizations; track org) {
                            <mat-chip
                              [matTooltip]="org.description || 'Organization: ' + org.login">
                              <img alt="GH" [src]="org.avatar_url" [alt]="org.login" class="org-avatar">
                              {{ org.login }}
                            </mat-chip>
                          }
                        </mat-chip-set>
                      </div>
                    </div>
                  }
                  <!-- Recent Repositories -->
                  @if (githubRepositories.length > 0) {
                    <div class="github-repositories">
                      <h4 class="mat-subtitle-2">Recent Repositories ({{ githubRepositories.length }})</h4>
                      <div class="repo-list">
                        <mat-chip-set>
                          @for (repo of githubRepositories.slice(0, 10); track repo) {
                            <mat-chip
                              [matTooltip]="repo.description || 'Repository: ' + repo.full_name"
                              [color]="repo.private ? 'accent' : 'primary'"
                              selected>
                              <mat-icon class="repo-icon">{{ repo.private ? 'lock' : 'public' }}</mat-icon>
                              {{ repo.name }}
                            </mat-chip>
                          }
                        </mat-chip-set>
                      </div>
                    </div>
                  }
                </div>
                <!-- Error Display -->
                @if (githubConnectionInfo.error) {
                  <div class="github-error">
                    <mat-icon color="warn">error</mat-icon>
                    <span>{{ githubConnectionInfo.error }}</span>
                  </div>
                }
                <!-- Action Buttons -->
                <div class="github-actions">
                  <button mat-button
                    color="primary"
                    (click)="refreshGitHubData()"
                    [disabled]="githubLoading">
                    <mat-icon>refresh</mat-icon>
                    @if (!githubLoading) {
                      <span>Refresh Data</span>
                    }
                    @if (githubLoading) {
                      <span>Refreshing...</span>
                    }
                  </button>
                  <button mat-button
                    color="warn"
                    (click)="disconnectGitHub()"
                    [disabled]="githubLoading">
                    <mat-icon>link_off</mat-icon>
                    Disconnect
                  </button>
                </div>
              </div>
            } @else {
              <div class="github-disconnected">
                <div class="disconnected-content">
                  <mat-icon class="large-icon" color="accent">info</mat-icon>
                  <h3 class="mat-subtitle-1">GitHub Account Not Connected</h3>
                  <p class="mat-body-2">
                    Your GitHub account is not currently connected. Connect your GitHub account to access repository management features.
                  </p>
                  <div class="features-list">
                    <div class="feature-item">
                      <mat-icon color="primary">check_circle</mat-icon>
                      <span>Access to organization repositories</span>
                    </div>
                    <div class="feature-item">
                      <mat-icon color="primary">check_circle</mat-icon>
                      <span>Create and manage pull requests</span>
                    </div>
                    <div class="feature-item">
                      <mat-icon color="primary">check_circle</mat-icon>
                      <span>Create and track issues</span>
                    </div>
                    <div class="feature-item">
                      <mat-icon color="primary">check_circle</mat-icon>
                      <span>Branch management capabilities</span>
                    </div>
                  </div>
                  <!-- Connect Button -->
                  <button mat-raised-button
                    color="primary"
                    class="connect-button"
                    (click)="connectGitHub()"
                    [disabled]="githubLoading">
                    <mat-icon>link</mat-icon>
                    @if (!githubLoading) {
                      <span>Connect GitHub Account</span>
                    }
                    @if (githubLoading) {
                      <span>Connecting...</span>
                    }
                  </button>
                  <!-- Error Display -->
                  @if (githubConnectionInfo.error) {
                    <div class="github-error">
                      <mat-icon color="warn">error</mat-icon>
                      <span>{{ githubConnectionInfo.error }}</span>
                      <p class="mat-caption">Contact support if this issue persists.</p>
                    </div>
                  }
                </div>
              </div>
            }
            <!-- Disconnected State -->
          </mat-card-content>
        </mat-card>
        <!-- User Settings Section -->
        <mat-card class="profile-section">
          <mat-card-header>
            <mat-card-title>User Settings</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <app-user-settings-editor
              [userSettings]="userSettings"
              [saveSuccess]="saveSuccess"
              [saveError]="saveError"
              (saveSettings)="onSaveSettings($event)"
              (addSetting)="onAddSetting($event)"
              (removeSetting)="onRemoveSetting($event)">
            </app-user-settings-editor>
          </mat-card-content>
        </mat-card>
      </div>
    </div>
  } @else {
    <mat-card class="auth-card mat-elevation-z3">
      <mat-card-header>
        <mat-card-title>
          <mat-icon color="warn">lock</mat-icon> Authentication Required
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <p>You must be logged in to view your profile and settings.</p>
      </mat-card-content>
      <mat-card-actions>
        <button mat-raised-button color="primary" (click)="userProfileService.login()">
          <mat-icon>login</mat-icon> Sign In
        </button>
      </mat-card-actions>
    </mat-card>
  }

  <!-- Not authenticated template -->
</div>
